import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CheckboxField,
  Field,
  PageHeader,
  SelectField,
  Skeleton,
  TextField,
  toast,
} from '@eon/ui';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { hooks } from '@eon/api-client';
import type { AdminUser } from '@eon/api-client';
import { ArrowLeft } from 'lucide-react';

const USER_ROLES = [
  'system_admin',
  'technical',
  'owner',
  'admin',
  'manager',
  'member',
  'user',
] as const;

const APP_TYPES = ['system', 'organization', 'customer'] as const;

function userToForm(user: AdminUser) {
  const addr = (user.address ?? {}) as Record<string, string>;
  return {
    name: user.name ?? '',
    surname: user.surname ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
    role: user.role ?? 'user',
    appType: user.appType ?? 'customer',
    active: user.active ?? true,
    language: user.language ?? 'tr',
    password: '',
    street: addr.street ?? '',
    city: addr.city ?? '',
    state: addr.state ?? '',
    country: addr.country ?? '',
    postalCode: addr.postalCode ?? '',
  };
}

const defaultForm = () => ({
  name: '',
  surname: '',
  email: '',
  phone: '',
  role: 'user',
  appType: 'customer',
  active: true,
  language: 'tr',
  password: '',
  street: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
});

export function UserNewPage() {
  return <UserEditForm mode="create" />;
}

export function UserEditPage() {
  const { userId } = useParams({ from: '/_app/users/$userId/edit' });
  return <UserEditForm mode="edit" userId={userId} />;
}

function UserEditForm({ mode, userId }: { mode: 'create' | 'edit'; userId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user, isLoading } = hooks.useAdminUser(userId ?? '');
  const create = hooks.useCreateUser();
  const update = hooks.useUpdateUser();
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (mode === 'edit' && user) {
      setForm(userToForm(user));
    }
  }, [mode, user]);

  const roleOptions = USER_ROLES.map((r) => ({
    value: r,
    label: t(`user.role.${r}`),
  }));
  const appOptions = APP_TYPES.map((a) => ({
    value: a,
    label: t(`user.appType.${a}`),
  }));

  const set = (key: keyof ReturnType<typeof defaultForm>, value: string | boolean) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) {
      toast.error(t('user.validation.required'));
      return;
    }
    if (mode === 'create' && form.password.trim().length < 6) {
      toast.error(t('user.validation.password'));
      return;
    }
    const address = {
      street: form.street.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
    };
    const hasAddress = Object.values(address).some(Boolean);
    try {
      if (mode === 'create') {
        const created = await create.mutateAsync({
          name,
          surname: form.surname.trim() || undefined,
          email,
          phone: form.phone.trim() || undefined,
          password: form.password,
          role: form.role,
          appType: form.appType,
          active: form.active,
          language: form.language,
          address: hasAddress ? address : undefined,
        });
        toast.success(t('user.created'));
        navigate({ to: '/users/$userId', params: { userId: created.id } });
      } else if (userId) {
        await update.mutateAsync({
          id: userId,
          name,
          surname: form.surname.trim() || undefined,
          email,
          phone: form.phone.trim() || undefined,
          role: form.role,
          appType: form.appType,
          active: form.active,
          language: form.language,
          address: hasAddress ? address : {},
          password: form.password.trim() || undefined,
        });
        toast.success(t('user.updated'));
        navigate({ to: '/users/$userId', params: { userId } });
      }
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  if (mode === 'edit' && isLoading) {
    return (
      <>
        <PageHeader title={t('user.editTitle')} />
        <Skeleton className="h-96 w-full" />
      </>
    );
  }

  if (mode === 'edit' && !user) {
    return (
      <>
        <PageHeader title={t('user.notFound')} />
        <Button variant="outline" asChild>
          <Link to="/users">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('user.back')}
          </Link>
        </Button>
      </>
    );
  }

  const pending = create.isPending || update.isPending;

  return (
    <>
      <PageHeader
        title={mode === 'create' ? t('user.createTitle') : t('user.editTitle')}
        actions={
          <Button variant="outline" asChild>
            <Link
              to={mode === 'edit' && userId ? '/users/$userId' : '/users'}
              {...(mode === 'edit' && userId ? { params: { userId } } : {})}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('user.back')}
            </Link>
          </Button>
        }
      />
      <form onSubmit={submit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('user.details')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label={t('user.name')}>
              <TextField value={form.name} onChange={(e) => set('name', e.target.value)} required />
            </Field>
            <Field label={t('user.surname')}>
              <TextField value={form.surname} onChange={(e) => set('surname', e.target.value)} />
            </Field>
            <Field label={t('user.email')}>
              <TextField
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
              />
            </Field>
            <Field label={t('user.phone')}>
              <TextField value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label={t('user.role.label')}>
              <SelectField value={form.role} onValueChange={(v) => set('role', v)} options={roleOptions} />
            </Field>
            <Field label={t('user.appType.label')}>
              <SelectField value={form.appType} onValueChange={(v) => set('appType', v)} options={appOptions} />
            </Field>
            <Field label={t('user.language')}>
              <TextField value={form.language} onChange={(e) => set('language', e.target.value)} />
            </Field>
            {mode === 'edit' ? (
              <div className="flex items-end pb-2">
                <CheckboxField
                  checked={form.active}
                  onCheckedChange={(v) => set('active', Boolean(v))}
                  label={t('user.active')}
                />
              </div>
            ) : null}
            <Field label={mode === 'create' ? t('user.password') : t('user.passwordOptional')} className="sm:col-span-2">
              <TextField
                type="password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                required={mode === 'create'}
                autoComplete="new-password"
              />
            </Field>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle>{t('user.addressSection')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label={t('user.address.street')} className="sm:col-span-2">
              <TextField value={form.street} onChange={(e) => set('street', e.target.value)} />
            </Field>
            <Field label={t('user.address.city')}>
              <TextField value={form.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            <Field label={t('user.address.state')}>
              <TextField value={form.state} onChange={(e) => set('state', e.target.value)} />
            </Field>
            <Field label={t('user.address.country')}>
              <TextField value={form.country} onChange={(e) => set('country', e.target.value)} />
            </Field>
            <Field label={t('user.address.postalCode')}>
              <TextField value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} />
            </Field>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to="/users">{t('actions.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={pending}>
              {mode === 'create' ? t('actions.create') : t('actions.save')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </>
  );
}
